"use client";

import { cn } from "@repo/ui/utils.ts";
import { Overline } from "@repo/ui/overline";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@repo/ui/accordion";
import { VisualWrapper } from "@/components/common/visual/visual.component";
import { RichTextCondensed } from "@/components/common/rich-text/rich-text-condensed.component";
import type { TimelineVerticalProps } from "./timeline-vertical.types";

export function TimelineVertical({
  overline,
  headline,
  body,
  entries,
  className,
}: TimelineVerticalProps) {
  const hasHeader = overline || headline || body;
  const hasEntries = entries && entries.length > 0;

  if (!hasEntries) return null;

  return (
    <section
      className={cn("bg-surface-base-primary", className)}
      data-testid="timeline-vertical"
    >
      <div className="container-grid py-16 desktop:py-28">
        <div className="col-span-full grid grid-cols-subgrid gap-y-8 desktop:gap-y-12">
          {/* Header */}
          {hasHeader && (
            <div className="col-span-full flex flex-col gap-4 desktop:gap-6 desktop:max-w-[644px]">
              {overline && <Overline>{overline}</Overline>}
              {headline && (
                <h2 className="typography-mobile-heading-h5 desktop:typography-desktop-heading-h5 text-text-primary">
                  {headline}
                </h2>
              )}
              {body && (
                <RichTextCondensed
                  richText={body}
                  className="text-text-secondary"
                />
              )}
            </div>
          )}

          {/* Timeline Accordion */}
          <div className="col-span-full">
            <Accordion type="single" collapsible defaultValue="item-0">
              {entries.map((entry, index) => (
                <AccordionItem key={entry._key} value={`item-${index}`}>
                  <AccordionTrigger className="[&>svg]:hidden cursor-pointer gap-6 py-9 [&[data-state=open]]:pb-0">
                    <span className="flex items-baseline justify-between w-full gap-6">
                      {entry.headline && (
                        <span className="typography-mobile-heading-subtitle-1 desktop:typography-desktop-heading-subtitle-1 text-text-primary text-left">
                          {entry.headline}
                        </span>
                      )}
                      {entry.year && (
                        <span className="typography-component-sm-medium text-text-primary whitespace-nowrap">
                          {entry.year}
                        </span>
                      )}
                    </span>
                  </AccordionTrigger>
                  {(entry.body || entry.visual) && (
                    <AccordionContent>
                      <div className="flex flex-col desktop:flex-row gap-10 desktop:gap-6 desktop:justify-between pt-10">
                        {entry.body && (
                          <div className="desktop:max-w-[644px]">
                            <RichTextCondensed
                              richText={entry.body}
                              className="text-text-secondary"
                            />
                          </div>
                        )}
                        {entry.visual && (
                          <div className="relative w-[309px] max-w-full shrink-0 overflow-hidden rounded-md">
                            <VisualWrapper
                              visual={entry.visual}
                              className="aspect-[309/232]"
                              visualClassName="w-full h-full"
                              imgClassName="object-cover"
                            />
                            <div className="absolute inset-0 bg-black/40 rounded-md pointer-events-none" />
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  )}
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
}
